import { useCallback } from 'react';
import { toaster } from 'baseui/toast';
import { useAtomValue, useSetAtom } from 'jotai';
import { NO_SAM_REGION_DETECTED, SAM_SEGMENTATION_REQUEST_FAILED } from '../../../../common/constant/constant';
import { useAPIProvider } from '../../../../common/hooks/use-api-provider';
import type { AnnotationUnderUserReviewT } from '../../../../common/types/types';
import { EVENTS } from '../../../labelling-v2/atoms/label-store-dispatch/events';
import { debouncedPolygonEpsilonAtom } from '../../../labelling-v2/atoms/tool-atoms';
import { useStageAtoms } from '../../../labelling-v2/components/stage-scope/hooks';
import { ImageSegmentationType } from '../../../types';
import useAnalyticsForSAM from '../../analytics/useSAMAnalytics';
import { getSAM3LabelAnalyticsPayload } from '../../components/toolbar/utils';
import { SAM_ANNOTATION_ACTIONS } from '../../constants';
import { base64MaskToPolygonResult, DEFAULT_POLYGON_EPSILON, douglasPeucker } from '../../utils/mask-to-polygon';
import { createAnnotationUnderReviewData, createSAMSegmentImagePayload, createSAMSegmentVideoPayload } from '../utils/label-store-utils';
import { usePendingPolygonReview } from './use-pending-polygon-review';
export const useSamForReview = () => {
  const {
    selectedAnnotationLabelAtom,
    dataUrlAtom,
    totalFramesAtom,
    dispatchAtom,
    currentFrameAnnotationUnderUserReviewAtom
  } = useStageAtoms();
  const selectedAnnotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const dataUrl = useAtomValue(dataUrlAtom);
  const totalFrames = useAtomValue(totalFramesAtom);
  const dispatch = useSetAtom(dispatchAtom);
  const setDebouncedPolygonEpsilon = useSetAtom(debouncedPolygonEpsilonAtom);
  const annotationUnderUserReview = useAtomValue(currentFrameAnnotationUnderUserReviewAtom);
  const {
    getSamSegmentData
  } = useAPIProvider();
  const {
    sendEventSam3MaskToPolygon
  } = useAnalyticsForSAM();
  const {
    pendingPolygonReview,
    setPendingReview,
    clearPendingReview
  } = usePendingPolygonReview();
  const hasPendingPolygonReview = pendingPolygonReview !== null && annotationUnderUserReview[pendingPolygonReview.annotationId] !== undefined;
  const updateEpsilon = useCallback((epsilon: number) => {
    if (!pendingPolygonReview || pendingPolygonReview.rawContour.length === 0) return;
    const annotation = annotationUnderUserReview[pendingPolygonReview.annotationId];
    if (!annotation) return;
    const points = douglasPeucker(pendingPolygonReview.rawContour, epsilon);
    const updatedAnnotation = createAnnotationUnderReviewData({
      color: annotation.annotationMetaData?.[0]?.annotationLabel?.style?.strokeColor ?? '',
      annotation
    }, points);
    dispatch({
      type: EVENTS.UPDATE_ANNOTATION_UNDER_REVIEW,
      annotationId: pendingPolygonReview.annotationId,
      annotation: updatedAnnotation
    });
  }, [dispatch, pendingPolygonReview, annotationUnderUserReview]);
  const addSamAnnotationForReview = useCallback((annotationId: string, annotation: AnnotationUnderUserReviewT) => {
    if (!selectedAnnotationLabel?.style?.strokeColor || !getSamSegmentData || !dataUrl) return;
    const capturedAnnotationLabel = selectedAnnotationLabel;
    dispatch({
      type: EVENTS.ADD_ANNOTATION_UNDER_REVIEW,
      annotations: {
        [annotationId]: annotation
      }
    });
    const samSegmentPayload = totalFrames > 1 ? createSAMSegmentVideoPayload(annotation, dataUrl, [annotation.frameIndex ?? 0, (annotation.frameIndex ?? 0) + 1]) : createSAMSegmentImagePayload(annotation, dataUrl);
    clearPendingReview();
    setDebouncedPolygonEpsilon(DEFAULT_POLYGON_EPSILON);
    const isPolygonMode = 'imageSegmentationType' in samSegmentPayload && samSegmentPayload.imageSegmentationType === ImageSegmentationType.IMAGE_SEGMENTATION_TYPE_POLYGON;
    getSamSegmentData?.(samSegmentPayload, totalFrames > 1 ? capturedAnnotationLabel.title : undefined).then(async dataList => {
      if (dataList instanceof Error) {
        toaster.negative(dataList.message);
        dispatch({
          type: EVENTS.DELETE_ANNOTATION_UNDER_REVIEW,
          annotationIds: [annotationId]
        });
        return;
      }
      if (!dataList) {
        toaster.negative(NO_SAM_REGION_DETECTED);
        dispatch({
          type: EVENTS.DELETE_ANNOTATION_UNDER_REVIEW,
          annotationIds: [annotationId]
        });
        return;
      }
      const {
        mask,
        automationRequestId
      } = dataList;
      let {
        points
      } = dataList;
      let rawContour: [number, number][] = [];
      if (isPolygonMode && mask) {
        const rawBase64Mask = mask.startsWith('data:') ? mask.slice(mask.indexOf(',') + 1) : mask;
        const result = await base64MaskToPolygonResult(rawBase64Mask);
        sendEventSam3MaskToPolygon({
          ...getSAM3LabelAnalyticsPayload(capturedAnnotationLabel),
          samTool: annotation.type === SAM_ANNOTATION_ACTIONS.BOUNDING_BOX_POLYGON ? 'SAM_BOUNDING_BOX' : 'SAM_CLICK',
          annotationType: 'POLYGON',
          success: result.points.length > 0,
          rawPointCount: result.rawPointCount,
          simplifiedPointCount: result.simplifiedPointCount,
          epsilonUsed: result.epsilonUsed
        });
        if (result.points.length === 0) {
          toaster.negative(NO_SAM_REGION_DETECTED);
          dispatch({
            type: EVENTS.DELETE_ANNOTATION_UNDER_REVIEW,
            annotationIds: [annotationId]
          });
          return;
        }
        points = result.points;
        rawContour = result.rawContour;
      }
      const updatedAnnotation = createAnnotationUnderReviewData({
        color: capturedAnnotationLabel.style?.strokeColor,
        annotation
      }, points, mask, automationRequestId);
      dispatch({
        type: EVENTS.UPDATE_ANNOTATION_UNDER_REVIEW,
        annotationId,
        annotation: updatedAnnotation
      });
      if (isPolygonMode && mask) {
        setPendingReview({
          annotationId,
          rawContour
        });
      }
    }).catch(error => {
      console.error('SAM segment data error:', error);
      toaster.negative(SAM_SEGMENTATION_REQUEST_FAILED);
      dispatch({
        type: EVENTS.DELETE_ANNOTATION_UNDER_REVIEW,
        annotationIds: [annotationId]
      });
    });
  }, [selectedAnnotationLabel, getSamSegmentData, dataUrl, dispatch, totalFrames, sendEventSam3MaskToPolygon, clearPendingReview, setPendingReview, setDebouncedPolygonEpsilon]);
  return {
    addSamAnnotationForReview,
    updateEpsilon,
    clearPendingPolygonReview: clearPendingReview,
    hasPendingPolygonReview
  };
};